import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { colors } from '../../utils/colors';
import { Container, FormField, TextArea, Button, SectionTitle } from '../common';
import { useEmailService, Notification } from '../send-email/send-email';
import { SocialNetworks } from '../social-networks/social-networks';
import { useAriaAnnouncer } from '../../utils/accessibility';
import { validators } from '../../utils/validation';

const ContactSection = styled.section`
  padding: 6rem 1rem;
  background: ${colors.background};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(to right, transparent, ${colors.border}, transparent);
  }
`;

const ContactGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 4rem;
  margin-top: 3rem;
  align-items: start;
  
  @media (min-width: 768px) {
    gap: 6rem;
  }
`;

const ContactInfo = styled(motion.div)`
  h3 {
    color: ${colors.textDark};
    margin-bottom: 2rem;
    font-size: 1.75rem;
    font-weight: 600;
  }
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const InfoItem = styled(motion.li)`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  color: ${colors.textLight};
  font-size: 1.1rem;

  i {
    color: ${colors.primary};
    font-size: 1.5rem;
    width: 24px;
    transition: transform 0.3s ease;
  }

  a {
    color: inherit;
    text-decoration: none;
    transition: color 0.3s ease;
    padding: 0.5rem 0;

    &:hover {
      color: ${colors.primary};
      i {
        transform: scale(1.1);
      }
    }
  }
`;

const ContactForm = styled(motion.form)`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  background: ${colors.white};
  padding: 2.5rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px ${colors.shadow};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${colors.primary};
    border-radius: 15px 15px 0 0;
  }
`;

// Honeypot field styling - completely hidden from users but visible to bots
const HoneypotField = styled.input`
  position: absolute !important;
  left: -9999px !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0,0,0,0) !important;
  white-space: nowrap !important;
  border: 0 !important;
  opacity: 0 !important;
  pointer-events: none !important;
  tab-index: -1 !important;
`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const formRef = useRef(null);
  const { submitContactForm, loading, notification, botProtection } = useEmailService('contact');
  const announce = useAriaAnnouncer();

  const validate = () => {
    const newErrors = {};
    
    // Basic required field validation
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    } else {
      // Anti-bot validation for name
      const humanNameCheck = validators.humanName(formData.name);
      if (!humanNameCheck.isValid) {
        newErrors.name = humanNameCheck.message;
      }
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    } else {
      // Anti-bot validation for email
      const tempEmailCheck = validators.noTempEmail(formData.email);
      if (!tempEmailCheck.isValid) {
        newErrors.email = tempEmailCheck.message;
      }
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'El mensaje es requerido';
    } else {
      // Anti-bot validation for message
      const spamCheck = validators.noSpamKeywords(formData.message);
      const repeatCheck = validators.noRepeatedChars(formData.message);
      const linkCheck = validators.maxLinks(2)(formData.message);
      const lengthCheck = validators.maxLength(2000)(formData.message);
      
      if (!spamCheck.isValid) {
        newErrors.message = spamCheck.message;
      } else if (!repeatCheck.isValid) {
        newErrors.message = repeatCheck.message;
      } else if (!linkCheck.isValid) {
        newErrors.message = linkCheck.message;
      } else if (!lengthCheck.isValid) {
        newErrors.message = lengthCheck.message;
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Record user activity for bot protection
    botProtection.recordActivity('typing');
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      announce('Por favor, corrija los errores en el formulario.', 'assertive');
      return;
    }

    const result = await submitContactForm(formData);
    
    if (result.success) {
      setFormData({ name: '', email: '', message: '' });
      announce('Mensaje enviado exitosamente.', 'polite');
    } else if (result.blocked) {
      if (result.isRateLimit) {
        // Clear form for rate limit messages since they indicate message was received
        setFormData({ name: '', email: '', message: '' });
        announce('Tu mensaje ya fue recibido. Nos pondremos en contacto pronto.', 'polite');
      } else {
        announce('Envío bloqueado por seguridad.', 'assertive');
      }
    } else {
      announce('Error al enviar el mensaje. Por favor, intente nuevamente.', 'assertive');
    }
  };

  return (
    <ContactSection id="contacto" aria-labelledby="contact-title">
      <Container>
        <SectionTitle
          title="Contacto"
          subtitle="¿Tienes alguna pregunta? Estoy aquí para ayudarte."
          centered
        />

        <ContactGrid
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <ContactInfo
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3>Información de Contacto</h3>
            <InfoList>
              {[
                {
                  icon: "fa-solid fa-phone",
                  href: "tel:+59891288376",
                  label: "Llamar al teléfono +598 91 288 376",
                  text: "+598 91 288 376"
                },
                {
                  icon: "fa-solid fa-envelope",
                  href: "mailto:psicologa.andrea.davila@gmail.com",
                  label: "Enviar email a psicologa.andrea.davila@gmail.com",
                  text: "psicologa.andrea.davila@gmail.com"
                },
                {
                  icon: "fa-solid fa-location-dot",
                  text: "Presencial: Pocitos, Montevideo, Uruguay",
                  isAddress: true
                },
                {
                  icon: "fa-solid fa-globe",
                  text: "Online: Sin restricciones geográficas",
                  isAddress: true
                }
              ].map((item, index) => (
                <InfoItem
                  key={item.icon}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                >
                  <i className={item.icon} aria-hidden="true" />
                  {item.isAddress ? (
                    <address>{item.text}</address>
                  ) : (
                    <a href={item.href} aria-label={item.label}>
                      {item.text}
                    </a>
                  )}
                </InfoItem>
              ))}
            </InfoList>

            <SocialNetworks 
              margin="3rem 0 0 0"
              size="45px"
              fontSize="1.25rem"
            />
          </ContactInfo>

          <ContactForm 
            ref={formRef}
            onSubmit={handleSubmit}
            noValidate
            aria-label="Formulario de contacto"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            onMouseMove={() => botProtection.recordActivity('mouse')}
          >
            {/* Honeypot field - invisible to users, visible to bots */}
            <HoneypotField
              name={botProtection.honeypotField}
              value={formData[botProtection.honeypotField] || ''}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
              aria-hidden="true"
            />
            
            <AnimatePresence mode="wait">
              {/* Notification will be shown outside the form */}
            </AnimatePresence>

            <FormField
              label="Nombre completo"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={errors.name}
              disabled={loading}
              required
              placeholder="Tu nombre completo"
              aria-required="true"
              aria-invalid={errors.name ? 'true' : 'false'}
              aria-describedby={errors.name ? 'name-error' : undefined}
              onFocus={() => botProtection.recordActivity('mouse')}
              onKeyDown={() => botProtection.recordActivity('keyboard')}
            />

            <FormField
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              disabled={loading}
              required
              placeholder="tu@email.com"
              aria-required="true"
              aria-invalid={errors.email ? 'true' : 'false'}
              aria-describedby={errors.email ? 'email-error' : undefined}
              onFocus={() => botProtection.recordActivity('mouse')}
              onKeyDown={() => botProtection.recordActivity('keyboard')}
            />

            <TextArea
              label="Mensaje"
              name="message"
              value={formData.message}
              onChange={handleChange}
              error={errors.message}
              disabled={loading}
              required
              minHeight="150px"
              placeholder="¿En qué puedo ayudarte?"
              aria-required="true"
              aria-invalid={errors.message ? 'true' : 'false'}
              aria-describedby={errors.message ? 'message-error' : undefined}
              onFocus={() => botProtection.recordActivity('mouse')}
              onKeyDown={() => botProtection.recordActivity('keyboard')}
            />

            <Button 
              type="submit"
              disabled={loading}
              icon={loading ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'}
              fullWidth
              aria-label={loading ? 'Enviando mensaje...' : 'Enviar mensaje'}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              {loading ? 'Enviando...' : 'Enviar Mensaje'}
            </Button>
          </ContactForm>
        </ContactGrid>
      </Container>

      {/* Notification from email service */}
      <AnimatePresence>
        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => {}} // The email service handles auto-hide
          />
        )}
      </AnimatePresence>
    </ContactSection>
  );
};
